import React, { useEffect, useState } from 'react';
import { Option, Radio } from '@material-tailwind/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { ReactSearchAutocomplete } from 'react-search-autocomplete';
import * as yup from 'yup';
import { ReactComponent as ErrorIcon } from '@assets/icons/info_error_light.svg';
import Button from '@components/forms/Button';
import Input from '@components/forms/Input';
import Select from '@components/forms/Select';
import { GET_ADDRESS, SEARCH_ADDRESS, post } from '@services';
import { useInvoiceDetailStore } from '@stores';
import { NAME_PATTERN, NAME_VALIDATION_MESSAGE } from '@utils/nameValidation';

type Party = 'buyer' | 'seller';

type Props = {
  party: Party;
  onPrevious?: () => void;
  onNext: () => void;
};

const phoneRegExp = /^(04\d{8}|\+614\d{8}|614\d{8})$/;
const entityTypes = ['INDIVIDUAL', 'BUSINESS', 'TRUST'] as const;
const trustHelperText =
  'Enter the trustee’s legal name followed by the trust name, for example: ABC Pty Ltd as trustee for the XYZ Trust.';

const states = [
  { value: '', label: 'Select a State' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NSW', label: 'New South Wales' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'WA', label: 'Western Australia' }
];

export const createPartySchema = (party: Party) =>
  yup
    .object({
      entityType: yup
        .string()
        .oneOf(entityTypes, `Please select a ${party} type.`)
        .required(`Please select a ${party} type.`),
      email: yup
        .string()
        .required('Please enter your email.')
        .email('Please provide a valid email address'),
      firstName: yup
        .string()
        .required(
          `Please enter the ${party === 'buyer' ? 'buyer’s' : 'seller’s'} name`
        )
        .matches(NAME_PATTERN, NAME_VALIDATION_MESSAGE),
      lastName: yup.string().when('entityType', {
        is: 'INDIVIDUAL',
        then: (schema) =>
          schema
            .required(
              `Please enter the ${
                party === 'buyer' ? 'buyer’s' : 'seller’s'
              } last name`
            )
            .matches(NAME_PATTERN, NAME_VALIDATION_MESSAGE),
        otherwise: (schema) => schema.notRequired()
      }),
      mobile: yup
        .string()
        .matches(
          phoneRegExp,
          'Invalid mobile number. Please enter a valid mobile.'
        )
        .required('Please enter your mobile.'),
      address: yup
        .string()
        .required(
          `Please provide the ${party === 'buyer' ? 'buyer’s' : 'seller’s'} address`
        ),
      state: yup.string().required('Please select a state.'),
      postcode: yup.string().required('Please enter the postcode.'),
      suburb: yup.string().required('Please enter the suburb.'),
      businessName: yup.string().when('entityType', {
        is: (value: string) => value === 'BUSINESS' || value === 'TRUST',
        then: (schema) =>
          schema
            .required('Please enter the business or trust name.')
            .matches(NAME_PATTERN, NAME_VALIDATION_MESSAGE),
        otherwise: (schema) => schema.notRequired()
      }),
      businessNumber: yup.string().when('entityType', {
        is: (value: string) => value === 'BUSINESS' || value === 'TRUST',
        then: (schema) =>
          schema
            .transform((value) => value?.replace(/\s/g, '') || '')
            .test(
              'business-number',
              'ABN must be 11 digits or ACN must be 9 digits.',
              (value) => !value || /^\d{9}$|^\d{11}$/.test(value)
            ),
        otherwise: (schema) => schema.notRequired()
      }),
      bsb:
        party === 'seller'
          ? yup.string().required('Please enter the BSB.')
          : yup.string().notRequired(),
      accountNumber:
        party === 'seller'
          ? yup.string().required('Please enter the account number.')
          : yup.string().notRequired()
    })
    .required();

const InvoicePartyDetailStep: React.FC<Props> = ({
  party,
  onPrevious,
  onNext
}) => {
  const title = party === 'buyer' ? 'Buyer' : 'Seller';
  const { invoiceDetailData, setInvoiceDetailData } = useInvoiceDetailStore();
  const partyData = invoiceDetailData[party] as any;
  const [items, setItems] = useState([{ text: '' }]);
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<any>({
    resolver: yupResolver(createPartySchema(party)),
    defaultValues: partyData
  });

  useEffect(() => {
    Object.entries(partyData).forEach(([field, value]) => {
      setValue(field, value ?? '');
    });
  }, [partyData, setValue]);

  const values = watch();
  const isEntity =
    values.entityType === 'BUSINESS' || values.entityType === 'TRUST';

  const onSubmit = (data: any) => {
    const updatedParty = {
      ...partyData,
      entityType: data.entityType,
      email: data.email,
      firstName: data.firstName,
      mobile: data.mobile,
      address: data.address,
      postcode: data.postcode,
      suburb: data.suburb,
      state: data.state,
      businessName: data.businessName,
      businessNumber: data.businessNumber,
      isDone: true,
      ...(data.entityType === 'INDIVIDUAL' && { lastName: data.lastName }),
      ...(party === 'seller' && {
        bsb: data.bsb,
        accountNumber: data.accountNumber
      })
    };

    setInvoiceDetailData({
      ...invoiceDetailData,
      [party]: updatedParty
    });
    onNext();
  };

  const handleOnSelect = (item: any) => {
    if (!item?.placeId) {
      setValue('suburb', '');
      setValue('state', '');
      setValue('postcode', '');
      return;
    }

    setValue('address', item.text.split(',')[0]);
    post(GET_ADDRESS, { placeId: item.placeId }).then((data: any) => {
      if (!data.place) return;

      setValue('suburb', data.place.neighborhood || '');
      const matchedState = states.find(
        (state) => state.label === data.place.region
      );
      setValue('state', matchedState?.value || '');
      setValue('postcode', data.place.postalCode || '');
    });
  };

  const handleOnSearch = (text: string) => {
    setValue('address', text);
    if (!text) return;

    post(SEARCH_ADDRESS, { text }).then((data: any) => {
      setItems(
        data.addresses.map((address: any, index: number) => ({
          ...address,
          id: index,
          displayText: address.text.split(',')[0]
        }))
      );
    });
  };

  const renderError = (field: string) => {
    const message = errors[field]?.message;
    return message ? (
      <span className="text-error-500 text-2sm mt-[6px]">
        {String(message)}
      </span>
    ) : null;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h3 className="text-xl font-semibold text-grey-900 mb-6">{title}</h3>
      <div className="mb-4">
        <p className="text-grey-700 text-sm mb-1.5 font-semibold">
          Is the {party} an individual, business or trust?
        </p>
        {entityTypes.map((entityType, index) => (
          <Radio
            key={entityType}
            name={`${party}-type`}
            containerProps={{
              className: `p-2 ${index > 0 ? 'ml-4' : ''}`
            }}
            checked={values.entityType === entityType}
            onChange={() => setValue('entityType', entityType)}
            label={
              entityType.charAt(0) + entityType.slice(1).toLocaleLowerCase()
            }
          />
        ))}
        {renderError('entityType')}
      </div>

      {values.entityType && (
        <>
          {isEntity && (
            <>
              <div className="mb-5">
                <p className="text-sm text-grey-700 font-medium mb-1.5">
                  {values.entityType === 'TRUST'
                    ? 'Trustee and trust name'
                    : 'Business or entity name'}
                </p>
                <Input
                  placeholder={
                    values.entityType === 'TRUST'
                      ? 'Trustee and trust name'
                      : 'Business or entity name'
                  }
                  {...register('businessName')}
                  icon={errors.businessName && <ErrorIcon />}
                  value={values.businessName || ''}
                />
                {values.entityType === 'TRUST' && (
                  <p className="text-sm text-grey-700 mt-1.5">
                    {trustHelperText}
                  </p>
                )}
                {renderError('businessName')}
              </div>
              <div className="mb-5">
                <p className="text-sm text-grey-700 font-medium mb-1.5">
                  {values.entityType === 'TRUST'
                    ? 'Trust ABN or trustee ACN, if applicable'
                    : 'ABN or ACN, if applicable'}
                </p>
                <Input
                  placeholder="ABN or ACN"
                  {...register('businessNumber')}
                  icon={errors.businessNumber && <ErrorIcon />}
                  value={values.businessNumber || ''}
                />
                {renderError('businessNumber')}
              </div>
              <h3 className="text-xl font-semibold text-grey-900 mb-6">
                Contact details
              </h3>
            </>
          )}

          <div className="mb-5">
            <p className="text-sm text-grey-700 font-medium mb-1.5">
              {isEntity ? 'Contact name' : 'First name'}
            </p>
            <Input
              placeholder={isEntity ? 'Contact name' : 'First name'}
              {...register('firstName')}
              icon={errors.firstName && <ErrorIcon />}
              value={values.firstName || ''}
            />
            {renderError('firstName')}
          </div>

          {!isEntity && (
            <div className="mb-5">
              <p className="text-sm text-grey-700 font-medium mb-1.5">
                Last name
              </p>
              <Input
                placeholder="Last name"
                {...register('lastName')}
                icon={errors.lastName && <ErrorIcon />}
                value={values.lastName || ''}
              />
              {renderError('lastName')}
            </div>
          )}

          <div className="mb-5">
            <p className="text-sm text-grey-700 font-medium mb-1.5">Email</p>
            <Input
              placeholder="Email"
              {...register('email')}
              value={values.email || ''}
              icon={errors.email && <ErrorIcon />}
            />
            {renderError('email')}
          </div>

          <div className="mb-5">
            <p className="text-sm text-grey-700 font-medium mb-1.5">Mobile</p>
            <Input
              placeholder="Mobile number"
              {...register('mobile')}
              value={values.mobile || ''}
              icon={errors.mobile && <ErrorIcon />}
            />
            {renderError('mobile')}
          </div>

          {isEntity && (
            <h3 className="text-xl font-semibold text-grey-900 mb-6">
              Principal place of business
            </h3>
          )}

          <div className="mb-5">
            <p className="text-sm text-grey-700 font-medium mb-1.5">Address</p>
            <ReactSearchAutocomplete
              items={items}
              fuseOptions={{ keys: ['text'] }}
              resultStringKeyName="displayText"
              inputDebounce={500}
              showNoResults={false}
              onSearch={handleOnSearch}
              formatResult={(item: any) => item.text}
              autoFocus={false}
              placeholder="Search Address"
              onSelect={handleOnSelect}
              showIcon={false}
              inputSearchString={values.address || ''}
              styling={{ borderRadius: '8px' }}
              className="!border-grey-300 focus:!border-blue-500 !text-base !shadow-none bg-white text-grey-900 z-50 custom-auto-complete"
            />
            {renderError('address')}
          </div>

          <div className="mb-5">
            <p className="text-sm text-grey-700 font-medium mb-1.5">Suburb</p>
            <Input
              placeholder="Suburb"
              {...register('suburb')}
              value={values.suburb || ''}
              icon={errors.suburb && <ErrorIcon />}
            />
            {renderError('suburb')}
          </div>

          <div className="mb-5 flex flex-row justify-start gap-5">
            <div className="min-w-[260px]">
              <span className="text-[14px] text-grey-700 font-medium">
                State
              </span>
              <Select
                placeholder="Select a State"
                {...register('state')}
                value={values.state || ''}
                onChange={(value) =>
                  setValue('state', value || '', { shouldValidate: true })
                }>
                {states.map((state) => (
                  <Option
                    key={state.value}
                    value={state.value}
                    disabled={!state.value}>
                    {state.label}
                  </Option>
                ))}
              </Select>
              {renderError('state')}
            </div>
            <div className="w-auto">
              <span className="text-[14px] text-grey-700 font-medium">
                Postcode
              </span>
              <Input
                placeholder="Postcode"
                {...register('postcode')}
                className="min-w-fit"
                value={values.postcode || ''}
                icon={errors.postcode && <ErrorIcon />}
              />
              {renderError('postcode')}
            </div>
          </div>

          {party === 'seller' && (
            <>
              <h3 className="text-xl font-semibold text-grey-900 mb-6">
                Bank account details
              </h3>
              <div className="mb-5">
                <p className="text-sm text-grey-700 font-medium mb-1.5">BSB</p>
                <Input
                  placeholder="BSB"
                  {...register('bsb')}
                  value={values.bsb || ''}
                  icon={errors.bsb && <ErrorIcon />}
                />
                {renderError('bsb')}
              </div>
              <div className="mb-5">
                <p className="text-sm text-grey-700 font-medium mb-1.5">
                  Account number
                </p>
                <Input
                  placeholder="Account number"
                  {...register('accountNumber')}
                  value={values.accountNumber || ''}
                  icon={errors.accountNumber && <ErrorIcon />}
                />
                {renderError('accountNumber')}
              </div>
            </>
          )}
        </>
      )}

      <div
        className={`flex border-t border-t-grey-200 pt-5 mt-4 gap-3 ${
          onPrevious ? 'justify-between' : 'justify-end'
        }`}>
        {onPrevious && (
          <Button
            type="button"
            className="bg-white text-grey-700 border-grey-300 !text-sm"
            variant="outlined"
            onClick={onPrevious}>
            Previous
          </Button>
        )}
        <Button type="submit" className="text-white bg-primary !text-sm">
          Next
        </Button>
      </div>
    </form>
  );
};

export default InvoicePartyDetailStep;
